/** Admin Dashboard rendered from the approved Android/iOS Figma Design Packages. */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent } from "react"
import { useNavigate } from "react-router-dom"
import { adminApi } from "../api/endpoints"
import { useAuthStore } from "../store/authStore"
import { useTelegram } from "../hooks/useTelegram"
import { useI18n } from "../hooks/useI18n"
import { adminUi, type AdminUiKey } from "../i18n/adminUi"
import type { AdminDashboard, AdminGarageRecord } from "../types/api"
import { ApiError } from "../api/client"
import { applyRussianPlateInputMask, formatPlateForDisplay, isValidRussianPlate } from "../utils/plate"
import { RussianFlagCircle, SearchIcon } from "./MainFigmaVisuals"
import { AppHeader } from "./AppHeader"
import { AdminMetricIcon, type AdminMetricKey, type AdminPlatform } from "./AdminMetricIconLibrary"
import { AdminArchiveSheet } from "./AdminArchiveSheet"
import adminEyeAndroid from "../assets/figma-main/eye-android.svg"
import adminEyeIos from "../assets/figma-main/eye-ios.svg"
import successAndroid from "../assets/figma-garage-lifecycle/success-android.svg"
import successIos from "../assets/figma-garage-lifecycle/success-ios.svg"
import { CountrySelectionSheet } from "./CountrySelectionSheet"
import { useRubberCanvas } from "../hooks/useRubberCanvas"
import "./AdminPage.figma.css"
import "./AdminCountryPicker.css"

type MetricRow = {
  key: AdminMetricKey
  labelKey: AdminUiKey
  value: number | string | null
}

const PERIODS = [
  { key: "24h", labelKey: "period24h" },
  { key: "7d", labelKey: "period7d" },
  { key: "30d", labelKey: "period30d" },
  { key: "1y", labelKey: "period1y" },
  { key: "all", labelKey: null },
] as const satisfies ReadonlyArray<{ key: "24h" | "7d" | "30d" | "1y" | "all"; labelKey: AdminUiKey | null }>

const FIGMA_ADMIN_LONG_VALUE = "9\u202f999\u202f999\u202f999\u202f999\u202f999\u202f999\u202f999\u202f999\u202f999"
const FIGMA_ARCHIVE_RECORDS: AdminGarageRecord[] = Array.from({ length: 9 }, (_, index) => ({
  garage_id: -(index + 1),
  vehicle_id: -(index + 1),
  plate_normalized: "A172MY888",
  plate_display: "A 172 MY 888",
  continuous_activation_started_at: "2023-05-12T00:00:00Z",
  continuous_renewals_count: 99,
}))

function isStaffUser(user: ReturnType<typeof useAuthStore.getState>["user"]) {
  return Boolean(
    user?.is_admin ||
      user?.is_owner ||
      user?.role === "admin" ||
      user?.role === "owner" ||
      user?.role === "moderation_admin",
  )
}

function formatMetric(value: number | string | null): string {
  if (typeof value === "string") return value
  if (value === null || !Number.isFinite(value)) return "0"
  return String(Math.trunc(value)).replace(/\B(?=(\d{3})+(?!\d))/g, "\u202f")
}

export function AdminPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { tg, showAlert } = useTelegram()
  const { locale, direction, t } = useI18n()
  const a = (key: AdminUiKey) => adminUi(locale, key)
  const plateInputRef = useRef<HTMLInputElement>(null)
  const [dashboard, setDashboard] = useState<AdminDashboard | null>(null)
  const [period, setPeriod] = useState<"24h" | "7d" | "30d" | "1y" | "all">("24h")
  const [plateInput, setPlateInput] = useState("")
  const [isPlateFocused, setIsPlateFocused] = useState(false)
  const [sheetMode, setSheetMode] = useState<"none" | "archive" | "search">("none")
  const [countryPickerOpen, setCountryPickerOpen] = useState(false)
  const [archiveRecords, setArchiveRecords] = useState<AdminGarageRecord[]>([])
  const [archiveCursor, setArchiveCursor] = useState<string | null>(null)
  const [target, setTarget] = useState<AdminGarageRecord | null>(null)
  const [armed, setArmed] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isUnlinking, setIsUnlinking] = useState(false)
  const [unlinkNotice, setUnlinkNotice] = useState(false)

  const figmaMode = import.meta.env.DEV ? new URLSearchParams(window.location.search).get("figma") : null
  const adminVisualMode = figmaMode?.startsWith("admin-android") || figmaMode?.startsWith("admin-ios") ? figmaMode : null
  const isIOS = adminVisualMode?.startsWith("admin-ios") || (!adminVisualMode && ["ios", "macos"].includes((tg?.platform || "").toLowerCase()))
  const platform: AdminPlatform = isIOS ? "ios" : "android"
  const {
    fitAreaRef,
    scale: adminScale,
    visualWidth: adminVisualWidth,
    canvasWidth: adminCanvasWidth,
    canvasInset: adminCanvasInset,
    referenceHeight: adminReferenceHeight,
  } = useRubberCanvas(710, 16)

  useEffect(() => {
    if (adminVisualMode) return
    if (!user) return
    if (!isStaffUser(user)) {
      navigate("/")
      return
    }

    let cancelled = false
    adminApi.getDashboard(period)
      .then((value) => {
        if (!cancelled) setDashboard(value)
      })
      .catch((err: any) => {
        if (!cancelled) showAlert(err?.message || a("loadError"))
      })
    return () => { cancelled = true }
  }, [adminVisualMode, user, navigate, showAlert, period, locale])

  useEffect(() => {
    if (!adminVisualMode) return
    if (adminVisualMode.includes("archive")) {
      setArchiveRecords(FIGMA_ARCHIVE_RECORDS)
      setSheetMode("archive")
    } else if (adminVisualMode.includes("search")) {
      setArchiveRecords(FIGMA_ARCHIVE_RECORDS)
      setTarget(FIGMA_ARCHIVE_RECORDS[4])
      setArmed(adminVisualMode.includes("armed"))
      setSheetMode("search")
    }
  }, [adminVisualMode])

  useEffect(() => {
    if (!unlinkNotice) return
    const timer = window.setTimeout(() => setUnlinkNotice(false), 3000)
    return () => window.clearTimeout(timer)
  }, [unlinkNotice])

  const reloadDashboard = useCallback(() => {
    if (adminVisualMode) return Promise.resolve()
    return adminApi.getDashboard(period).then(setDashboard)
  }, [adminVisualMode, period])

  const openArchive = async () => {
    if (adminVisualMode) return
    try {
      const page = await adminApi.getGarageArchive()
      setArchiveRecords(page.items)
      setArchiveCursor(page.next_cursor)
      setTarget(null)
      setArmed(false)
      setSheetMode("archive")
    } catch (err: any) {
      showAlert(err instanceof ApiError ? err.detail : a("archiveLoadError"))
    }
  }

  const loadMore = async () => {
    if (!archiveCursor || isLoadingMore || adminVisualMode) return
    setIsLoadingMore(true)
    try {
      const page = await adminApi.getGarageArchive(archiveCursor)
      setArchiveRecords((current) => [...current, ...page.items.filter((item) => !current.some((old) => old.garage_id === item.garage_id))])
      setArchiveCursor(page.next_cursor)
    } catch (err: any) {
      showAlert(err instanceof ApiError ? err.detail : a("archiveLoadError"))
    } finally {
      setIsLoadingMore(false)
    }
  }

  const searchGarage = async (event?: FormEvent) => {
    event?.preventDefault()
    if (adminVisualMode) return
    if (!isValidRussianPlate(plateInput)) {
      showAlert(a("invalidPlate"))
      return
    }
    try {
      const [found, page] = await Promise.all([adminApi.searchGarage(plateInput), adminApi.getGarageArchive()])
      setTarget(found)
      setArchiveRecords(page.items)
      setArchiveCursor(page.next_cursor)
      setArmed(false)
      setSheetMode("search")
    } catch (err: any) {
      showAlert(err instanceof ApiError ? err.detail : a("searchError"))
    }
  }

  const selectArchiveRecord = (record: AdminGarageRecord) => {
    if (adminVisualMode) return
    if (sheetMode === "search" && target?.garage_id === record.garage_id) {
      setTarget(null)
      setPlateInput("")
      setArmed(false)
      setSheetMode("archive")
      return
    }
    setTarget(record)
    setPlateInput(record.plate_normalized)
    setArmed(false)
    setSheetMode("search")
  }

  const closeSheet = () => {
    if (adminVisualMode) return
    if (sheetMode === "search") setPlateInput("")
    setSheetMode("none")
    setTarget(null)
    setArmed(false)
  }

  const closeCountryPicker = () => {
    if (adminVisualMode) return
    setCountryPickerOpen(false)
  }

  const openCountryPicker = () => {
    if (adminVisualMode) return
    if (sheetMode !== "none") closeSheet()
    plateInputRef.current?.blur()
    setCountryPickerOpen(true)
  }

  const toggleArchive = () => {
    if (adminVisualMode) return
    if (countryPickerOpen) {
      setCountryPickerOpen(false)
      void openArchive()
      return
    }
    if (sheetMode !== "none") {
      closeSheet()
      return
    }
    void openArchive()
  }

  const confirmUnlink = async () => {
    if (!target || !armed || isUnlinking || adminVisualMode) return
    setIsUnlinking(true)
    try {
      await adminApi.unlinkGarage(target)
      setArchiveRecords((current) => current.filter((item) => item.garage_id !== target.garage_id))
      await reloadDashboard().catch(() => undefined)
      closeSheet()
      setUnlinkNotice(true)
    } catch (err: any) {
      setArmed(false)
      showAlert(err instanceof ApiError ? err.detail : a("unlinkError"))
    } finally {
      setIsUnlinking(false)
    }
  }

  const metrics = useMemo<MetricRow[]>(() => {
    const rows: MetricRow[] = [
      { key: "balance", labelKey: "balance", value: dashboard?.stars_balance ?? 0 },
      { key: "income", labelKey: "income", value: dashboard?.income_stars ?? 0 },
      { key: "users", labelKey: "users", value: dashboard?.total_users ?? 0 },
      { key: "numbers", labelKey: "numbers", value: dashboard?.total_vehicles ?? 0 },
      { key: "garages", labelKey: "garages", value: dashboard?.active_garages ?? 0 },
      { key: "autoclans", labelKey: "autoclans", value: dashboard?.total_autoclans ?? 0 },
      { key: "companions", labelKey: "companions", value: dashboard?.total_partners ?? 0 },
      { key: "qrcodes", labelKey: "qrcodes", value: 0 },
      { key: "shared", labelKey: "shared", value: dashboard?.card_share_count ?? 0 },
      { key: "transitions", labelKey: "transitions", value: dashboard?.link_click_count ?? 0 },
      { key: "signals", labelKey: "signals", value: dashboard?.total_signals ?? 0 },
      { key: "urgent", labelKey: "urgent", value: dashboard?.urgent_signals ?? 0 },
      { key: "road", labelKey: "road", value: dashboard?.road_signals ?? 0 },
      { key: "personal", labelKey: "personal", value: dashboard?.attention_signals ?? 0 },
      { key: "autotrade", labelKey: "autotrade", value: 0 },
      { key: "pitstop", labelKey: "pitstop", value: 0 },
      { key: "insurance", labelKey: "insurance", value: 0 },
      { key: "autohelp", labelKey: "autohelp", value: 0 },
    ]
    if (!adminVisualMode) return rows
    return rows.map((metric): MetricRow => ({ ...metric, value: FIGMA_ADMIN_LONG_VALUE }))
  }, [adminVisualMode, dashboard])

  if (!adminVisualMode && !user) return null
  if (!adminVisualMode && !isStaffUser(user)) return null

  return (
    <div className={`bbf-admin-screen ${isIOS ? "platform-ios" : "platform-android"}`}>
      <AppHeader
        left={{ label: t("common.back"), onClick: () => navigate("/") }}
        right={{ label: a("archive"), onClick: toggleArchive, tone: "primary" }}
      />

      <div className="bbf-admin-fit-area" ref={fitAreaRef}>
        <div
          className="bbf-admin-scale-box"
          style={{
            width: `${adminVisualWidth}px`,
            height: `${adminReferenceHeight * adminScale}px`,
          }}
        >
          <div
            className="bbf-admin-canvas"
            style={{
              width: `${adminCanvasWidth}px`,
              height: `${adminReferenceHeight}px`,
              transform: `scale(${adminScale})`,
              "--bbf-admin-canvas-inset": `${adminCanvasInset}px`,
              "--bbf-rail-width": `${adminCanvasWidth}px`,
              "--bbf-rail-left": "0px",
              "--bbf-safe-bottom": "0px",
            } as CSSProperties}
          >
      <main className="bbf-admin-body">
      <form className="bbf-admin-search" aria-label={a("searchByPlate")} onSubmit={searchGarage}>
        <button
          type="button"
          className="bbf-admin-search-country"
          aria-label={t("countrySelection.openAria")}
          onClick={openCountryPicker}
        >
          <RussianFlagCircle />
        </button>
        <input
          ref={plateInputRef}
          className="bbf-admin-search-input"
          type="text"
          placeholder={isPlateFocused ? "" : "А 000 АА 000"}
          value={formatPlateForDisplay(plateInput)}
          onFocus={() => setIsPlateFocused(true)}
          onBlur={() => setIsPlateFocused(false)}
          onChange={(event) => setPlateInput(applyRussianPlateInputMask(event.target.value))}
          readOnly={Boolean(adminVisualMode)}
          inputMode="text"
          autoComplete="off"
          aria-label={a("plate")}
        />
        <button type="submit" className="bbf-admin-search-action" aria-label={a("findPlate")}>
          <SearchIcon />
        </button>
      </form>

      <nav className="bbf-admin-periods" aria-label={a("statsPeriod")}>
        {PERIODS.map((item) => (
          <span className="bbf-admin-period-slot" key={item.key}>
            <button
              type="button"
              className={`bbf-admin-period ${item.key === period ? "active" : ""}`}
              onClick={() => setPeriod(item.key)}
            >
              <span className="bbf-admin-period-visual">
                <span className="bbf-admin-period-label" dir={direction}>{item.labelKey ? a(item.labelKey) : "∞"}</span>
              </span>
            </button>
          </span>
        ))}
      </nav>

      {sheetMode === "none" && (
        <p className="bbf-admin-search-summary" dir={direction}>
          <span className="bbf-admin-launch-summary-content">
            <span className="bbf-admin-launch-eye-badge" aria-hidden="true">
              <span
                className="bbf-admin-launch-eye"
                style={{
                  "--bbf-admin-launch-eye-mask": `url("${isIOS ? adminEyeIos : adminEyeAndroid}")`,
                } as CSSProperties}
              />
            </span>
            <span className="bbf-admin-launch-count">{formatMetric(dashboard?.app_launch_count ?? 0)}</span>
          </span>
        </p>
      )}

      {sheetMode === "search" && target && (
        <p className="bbf-admin-search-summary" dir={direction}>
          {a("activationDate")}: {new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(target.continuous_activation_started_at))} / {a("renewals")}: {target.continuous_renewals_count}
        </p>
      )}

      <section className="bbf-admin-metrics" aria-label={a("statistics")}>
        {metrics.map((metric) => (
          <div className="bbf-admin-metric-row" key={metric.key}>
            <span className="bbf-admin-metric-value-pill" aria-hidden="true" />
            <span className="bbf-admin-metric-label-pill" aria-hidden="true" />
            <span className="bbf-admin-metric-value">{formatMetric(metric.value)}</span>
            <AdminMetricIcon platform={platform} metric={metric.key} />
            <span className="bbf-admin-metric-label" dir={direction}>{a(metric.labelKey)}</span>
          </div>
        ))}
      </section>
      </main>
          </div>
        </div>
      </div>

      {countryPickerOpen && (
        <div className="bbf-admin-country-overlay" onPointerDown={closeCountryPicker} role="presentation">
          <div onPointerDown={(event) => event.stopPropagation()}>
            <CountrySelectionSheet
              title={t("countrySelection.chooseCountry")}
              onBack={closeCountryPicker}
              onNext={closeCountryPicker}
            />
          </div>
        </div>
      )}

      {unlinkNotice && (
        <div className="bbf-admin-unlink-notice" role="status" aria-live="polite">
          <div className="bbf-admin-unlink-notice-content">
            <img src={isIOS ? successIos : successAndroid} alt="" />
            <span dir={direction}>{a("garageUnlinked")}</span>
          </div>
        </div>
      )}

      {sheetMode !== "none" && (
        <div className="bbf-admin-archive-overlay" onPointerDown={closeSheet} role="presentation">
          <div onPointerDown={(event) => event.stopPropagation()}>
            <AdminArchiveSheet
              mode={sheetMode}
              records={archiveRecords}
              target={target}
              armed={armed}
              busy={isUnlinking}
              nextCursor={archiveCursor}
              visualScrollTop={adminVisualMode?.includes("scrolled") ? (sheetMode === "archive" ? 9 : 38) : 0}
              onArm={() => setArmed(true)}
              onSelect={selectArchiveRecord}
              onBack={closeSheet}
              onConfirm={confirmUnlink}
              onLoadMore={loadMore}
            />
          </div>
        </div>
      )}
    </div>
  )
}
